import _ from "lodash";

/**
 * Merges two arrays of objects based on a specified key or key function.
 * Uses Lodash's merge for deep object merging, maintaining the structure of arr1 as base.
 * Objects from arr1 are merged with matching objects from arr2 based on the specified key.
 *
 * @param {Array<Object>} arr1 - The first array of objects (base array)
 * @param {Array<Object>} arr2 - The second array of objects to merge with arr1
 * @param {string|Function} key - Either:
 *                               - A string representing the key to match objects by (uses Lodash's get for nested paths)
 *                               - A function that returns the key for an object
 * @returns {Array<Object>} Returns:
 *                         - An array of merged objects if both arrays have content (similar to left join)
 *                         - Returns arr1 or arr2 if one is nullish
 *                         - Empty array if both arrays are nullish
 *
 * @example
 * // Basic usage with string key
 * const arr1 = [{ id: 1, name: 'John' }];
 * const arr2 = [{ id: 1, age: 30 }];
 * mergeTwoObjectArraysByKeys(arr1, arr2, 'id');
 * // => [{ id: 1, name: 'John', age: 30 }]
 *
 * @example
 * // Using nested path as key
 * const arr1 = [{ user: { id: 1 }, name: 'John' }];
 * const arr2 = [{ user: { id: 1 }, role: 'admin' }];
 * mergeTwoObjectArraysByKeys(arr1, arr2, 'user.id');
 * // => [{ user: { id: 1 }, name: 'John', role: 'admin' }]
 *
 * @example
 * // Using custom key function
 * const arr1 = [{ userId: 1, name: 'John' }];
 * const arr2 = [{ id: 1, age: 30 }];
 * mergeTwoObjectArraysByKeys(arr1, arr2, item => item.userId || item.id);
 * // => [{ userId: 1, name: 'John', age: 30 }]
 *
 * @example
 * // Handling empty arrays
 * const arr1 = [{ id: 1, name: 'John' }];
 * const arr2 = [];
 * mergeTwoObjectArraysByKeys(arr1, arr2, 'id');
 * // => [{ id: 1, name: 'John' }]
 */
export const mergeTwoObjectArraysByKeys = (arr1, arr2, key) => {
  //allows string based key or function based key
  const getKey = item => (_.isFunction(key) ? key(item) : _.get(item, key));

  if (!_.isEmpty(arr1) && !_.isEmpty(arr2)) {
    const arr2KeyMap = _.keyBy(arr2, getKey);

    const merged = _.map(arr1, item =>
      _.merge({}, item, arr2KeyMap[getKey(item)])
    );

    return merged;
  }
  return arr1 || arr2 || [];
};

/*
 **
 * Merges multiple arrays of objects based on different keys
 *
 * @param {Array<{ arr: Object[], key: string | function, mergedPath: string, getValue: function }>} mergeConfig
 * @returns {Object[]} Merged array
 */
export const mergeObjectArraysByDifferentKeysWithCustomValue = mergeConfig => {
  if (_.isEmpty(mergeConfig) || !Array.isArray(mergeConfig)) return [];

  const [base, ...rest] = mergeConfig;

  const getKeyFn = key => item =>
    _.isFunction(key) ? key(item) : _.get(item, key);

  const baseKeyFn = getKeyFn(base.key);

  const baseTransformFunction = base?.getValue;

  const idsToPropertiesMapArray = _.map(rest, obj => _.keyBy(obj.arr, obj.key));

  // Start with the base array
  let result = base.arr;

  result = _.map(result, item => {
    const transformedBaseItem = baseTransformFunction
      ? baseTransformFunction(item)
      : item;
    const restMatch = _.map(
      idsToPropertiesMapArray,
      (idsToPropertiesMap, index) => {
        const currentMergeConfig = mergeConfig[index + 1];
        const { mergedPath, getValue } = currentMergeConfig || {};

        const value = idsToPropertiesMap[baseKeyFn(item)];
        if (!value) return {};
        if (mergedPath) {
          const valueToSet = getValue ? getValue(value) : value;
          const objectWithMergedPath = {};
          _.set(objectWithMergedPath, mergedPath, valueToSet);
          return objectWithMergedPath;
        }
        return value;
      }
    );
    return _.merge({}, transformedBaseItem, ...restMatch);
  });

  return result;
};
